import moment from 'moment'

const EASTERN = 'America/New_York'

const dateFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN,
  month: 'short',
  day: 'numeric',
  year: 'numeric',
})

const dateFmtNoYear = new Intl.DateTimeFormat('en-US', {
  timeZone: EASTERN,
  month: 'short',
  day: 'numeric',
})

export function maskToken(token: string): string {
  const t = token.trim()
  if (t.length <= 4) return '••••'
  return `••••-${t.slice(-4)}`
}

export function formatBatchRef(batchId: number, createdAt?: string): string {
  const y = createdAt ? new Date(createdAt).getUTCFullYear() : new Date().getFullYear()
  return `BV-${y}-${String(batchId).padStart(4, '0')}`
}

export function formatPeriodLabel(startUtc?: string, endUtc?: string): string {
  if (!startUtc || !endUtc) return 'Current batch'
  const start = new Date(startUtc)
  const end = new Date(endUtc)
  const y = end.getUTCFullYear()
  if (start.getUTCFullYear() === y && start.getUTCMonth() === end.getUTCMonth()) {
    return `${dateFmtNoYear.format(start)} – ${dateFmtNoYear.format(end)}, ${y}`
  }
  if (start.getUTCFullYear() === y) {
    return `${dateFmtNoYear.format(start)} – ${dateFmt.format(end)}`
  }
  return `${dateFmt.format(start)} – ${dateFmt.format(end)}`
}

export function formatDueLabel(expiresUtc?: string): string {
  if (!expiresUtc) return 'See invitation email'
  return dateFmt.format(new Date(expiresUtc))
}

export function formatSubmitted(submittedAt?: string): string {
  if (!submittedAt) return '—'
  return dateFmt.format(new Date(submittedAt))
}

export function parseDurationMinutes(duration?: string): number {
  if (!duration) return 0
  let total = 0
  const h = duration.match(/(\d+)h/)
  const m = duration.match(/(\d+)m/)
  const s = duration.match(/(\d+)s/)
  if (h) total += Number(h[1]) * 60
  if (m) total += Number(m[1])
  if (s && !h && !m) total += Math.ceil(Number(s[1]) / 60)
  return total
}

export function formatTotalMinutes(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60)
  const r = totalMinutes % 60
  if (!h) return `${r}m`
  return r ? `${h}h ${r}m` : `${h}h`
}

export function formatTravelOrig(h?: number, m?: number): string {
  return `${h ?? 0}h ${m ?? 0}m`
}

export function formatMileageOrig(mi?: number): string {
  if (mi == null) return '—'
  return `${mi} mi`
}

/** Portal display: 614 instead of CAL-614-XB (API may already normalize). */
export function formatBulkVosCallId(callId?: string | null): string {
  if (!callId?.trim()) return '—'
  const trimmed = callId.trim()
  const match = trimmed.match(/^CAL-(\d+)/i)
  if (match) return match[1]
  return trimmed
}

/** Portal display: 942363 instead of INT-942363. */
export function formatBulkVosInterpreterId(interpreterId?: string | null): string {
  if (!interpreterId?.trim() || interpreterId.trim() === '—') return '—'
  const trimmed = interpreterId.trim()
  if (/^INT-/i.test(trimmed)) return trimmed.slice(4)
  return trimmed
}

export function formatBulkVosRecordId(recordId?: string | null): string {
  if (!recordId?.trim()) return '—'
  return recordId.trim()
}

/** Match appointment meeting summary display (e.g. 08:59 PM). API times are HH:mm:ss in appointment TZ. */
const BULK_VOS_TIME_PARSE_FORMATS = ['HH:mm:ss', 'HH:mm', 'hh:mm A', 'h:mm A'] as const

export function formatBulkVosGridTime(value?: string | null): string {
  if (!value?.trim()) return '—'
  const parsed = moment(value.trim(), [...BULK_VOS_TIME_PARSE_FORMATS], true)
  return parsed.isValid() ? parsed.format('hh:mm A') : value.trim()
}

/** For editable fields (empty string instead of em dash). */
export function formatBulkVosTimeInput(value?: string | null): string {
  if (!value?.trim()) return ''
  const parsed = moment(value.trim(), [...BULK_VOS_TIME_PARSE_FORMATS], true)
  return parsed.isValid() ? parsed.format('hh:mm A') : value.trim()
}

/** API / BulkVosTimeParser accepts several formats; store canonical HH:mm:ss. */
export function normalizeBulkVosTimeForApi(value?: string | null): string | undefined {
  if (value == null || !value.trim()) return undefined
  const parsed = moment(value.trim(), [...BULK_VOS_TIME_PARSE_FORMATS], true)
  return parsed.isValid() ? parsed.format('HH:mm:ss') : value.trim()
}

function isLikelyApiHost(appUrl: string, apiUrl: string): boolean {
  const apiHost = apiUrl.replace(/\/api\/?$/i, '').replace(/\/$/, '')
  const app = appUrl.replace(/\/$/, '')
  return Boolean(apiHost && app === apiHost)
}

/** Public SPA base for /bulk-vos/:token (same host you use for the Connect UI, not the API host). */
export function resolveBulkVosPortalBaseUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '')
  }
  const appUrl = (process.env.REACT_APP_APP_URL || '').replace(/\/$/, '')
  const apiUrl = process.env.REACT_APP_API_URL || ''
  if (appUrl && !isLikelyApiHost(appUrl, apiUrl)) return appUrl
  return appUrl
}

export function buildBulkVosPortalUrl(portalToken: string): string {
  const base = resolveBulkVosPortalBaseUrl()
  return `${base}/bulk-vos/${encodeURIComponent(portalToken.trim())}`
}
